/**
 * ProjectDetailsForm (ASS-80 / ASS-82)
 *
 * The Project/Yard Details fields, shared between the New-project wizard
 * (`ProjectSetupWizard.jsx`) and the Edit-project modal
 * (`ProjectListPage.jsx`) so the two screens can't drift apart again --
 * Mark, 2026-09-10: "The current manual project edit screen is it's own
 * version of things and should match the new manual project entry screen."
 *
 * Fields, same order on both screens: name, type + status controls,
 * client, dates, description, address, latitude, longitude.
 *
 * KMZ upload and the boundary selector are NOT part of this component --
 * they stay in the wizard, outside it. Mark overrides N5's optional
 * boundary file for this form; N6's drop-a-pin control is deferred and
 * intentionally absent.
 *
 * Controlled component: the caller owns the value object and passes a
 * single onChange(patch) that merges into it -- same pattern the wizard
 * already used for projectData, so lifting it here didn't change the
 * call-site shape.
 *
 * Location validity is exported as a pure function (isLocationSatisfied)
 * so both callers gate their Save button on the exact same rule instead
 * of each re-deriving it (the original bug this ticket exists to prevent).
 *
 * ASS-82: address-only locations (no lat/lng entered) are geocoded on Save
 * via Mapbox. resolveLocationForSave(value) is the single place both
 * callers invoke -- entered coordinates always win and skip the network
 * request entirely; a geocode failure blocks Save with the stated copy;
 * a successful geocode returns { address, lat, lng, source: 'geocoded' }.
 * If the Mapbox token itself is bad (401), this throws a distinct error
 * rather than silently falling through, per the brief's "stop and say so,
 * don't swap tokens" instruction.
 *
 * ASS-82 follow-up: Mapbox almost never returns zero results, so "zero
 * results" alone can't catch a bad address -- "qwxzqwxz nowhere" resolved
 * to Nowhere, Oklahoma at relevance 0.5. Two changes: (1) restrict the
 * search to `country=us,ca` (this app has no international customers
 * today); (2) treat `feature.relevance < 0.9` the same as no result. No
 * threshold catches everything -- "123 Main St" and literal "nowhere"
 * both score 1.0 -- so resolveLocationForSave also returns Mapbox's
 * resolved `place_name` as `placeName`, and the caller shows it back to
 * the user so they can catch a wrong-but-high-confidence match themselves.
 */

import React from 'react';
import {
  Alert,
  Box,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Shared validity helpers -- both ProjectSetupWizard and the Edit modal
// import these rather than re-deriving the rule locally.

// AST-95: coordinates pasted from documents, chat or email often carry a
// typographic minus (U+2212), an en/em dash, a non-breaking space or a
// trailing degree sign. Number('\u221294.1') is NaN, and a type="number" input
// silently drops the sign -- which is how a Brainerd, MN project landed in
// Mongolia. Normalise to plain ASCII before parsing.
const MINUS_LIKE = /[\u2212\u2012\u2013\u2014\u2015\uFE58\uFE63\uFF0D]/g;
const SPACE_LIKE = /[\s\u00A0\u2007\u202F]/g;
const DEGREE = /[\u00B0\u00BA]/g;
export function normaliseCoordinateInput(value) {
  if (value == null) return '';
  return String(value)
    .replace(MINUS_LIKE, '-')
    .replace(SPACE_LIKE, '')
    .replace(DEGREE, '')
    .replace(/,/g, '.');
}

function parseCoordinate(value) {
  const v = normaliseCoordinateInput(value);
  if (v === '' || !/^[-+]?(\d+\.?\d*|\.\d+)$/.test(v)) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function parseLocationLat(value) {
  return parseCoordinate(value);
}

export function parseLocationLng(value) {
  return parseCoordinate(value);
}

// AST-95: every customer is in the US or Canada (geocoding is limited to
// country=us,ca below). A point outside North America almost always means
// a dropped or flipped sign. Warn -- never block -- and suggest the fix.
// Rough North America box incl. Alaska, Hawaii and Puerto Rico.
const NA_BOUNDS = { minLat: 15, maxLat: 72, minLng: -170, maxLng: -50 };
const inNorthAmerica = (lat, lng) => lat >= NA_BOUNDS.minLat && lat <= NA_BOUNDS.maxLat
  && lng >= NA_BOUNDS.minLng && lng <= NA_BOUNDS.maxLng;

function regionName(lat, lng) {
  if (lat < 0 && lng > 110) return 'Australia';
  if (lat < -10 && lng < -30) return 'South America';
  if (lat < 0) return 'the southern hemisphere';
  if (lng > 60) return 'Asia';
  if (lng > -20) return 'Europe or Africa';
  return 'outside North America';
}

/**
 * Returns null when the point looks right (or isn't complete yet), otherwise
 * { message, suggestion: { lat, lng } | null }.
 */
export function coordinateWarning(latValue, lngValue) {
  const lat = parseLocationLat(latValue);
  const lng = parseLocationLng(lngValue);
  if (!isValidLat(lat) || !isValidLng(lng) || inNorthAmerica(lat, lng)) return null;
  const fix = [[lat, -lng], [-lat, lng], [-lat, -lng]].find(([a, b]) => isValidLat(a) && isValidLng(b) && inNorthAmerica(a, b));
  const where = regionName(lat, lng);
  const place = where.startsWith('the ') || where.startsWith('outside') ? where : `in ${where}`;
  if (!fix) return { message: `This point is ${place}. Check the latitude and longitude.`, suggestion: null };
  const [sLat, sLng] = fix;
  const changed = sLat !== lat ? `latitude ${sLat}` : `longitude ${sLng}`;
  return { message: `This point is ${place}. Did you mean ${changed}?`, suggestion: { lat: sLat, lng: sLng } };
}

export function isValidLat(n) {
  return n !== null && n >= -90 && n <= 90;
}

export function isValidLng(n) {
  return n !== null && n >= -180 && n <= 180;
}

// value: { locationAddress, locationLat, locationLng } (flat form-layer
// strings, same shape ProjectDetailsForm's fields read/write).
export function isLocationSatisfied(value) {
  const lat = isValidLat(parseLocationLat(value.locationLat));
  const lng = isValidLng(parseLocationLng(value.locationLng));
  const hasAddress = (value.locationAddress || '').trim().length > 0;
  return hasAddress || (lat && lng);
}

// value: { name }
export function isNameSatisfied(value) {
  return (value.name || '').trim().length > 0;
}

// ASS-82: distinct error types so callers can show the exact copy the
// brief specifies, and so a 401 (bad/missing token) never gets treated the
// same as "no results for this address" -- swapping tokens or silently
// retrying on a 401 is explicitly out of scope; the caller must stop and
// say so.
export class GeocodeNotFoundError extends Error {}
export class GeocodeAuthError extends Error {}

// value: { locationAddress, locationLat, locationLng }. Returns a promise
// resolving to { address, lat, lng, source, placeName } ready to send as
// the `location` wire object (placeName is extra metadata, not required
// by the wire shape, but callers persist it so Edit can show it back).
// Entered coordinates always win -- if both lat and lng are already
// valid, this never makes a network request at all (verify via the
// network tab, per the ticket's falsifier).
//
// ASS-82 follow-up: the relevance-below-0.9 guard. Mapbox's own
// calibration for this app's real addresses (Superior WI / Duluth MN /
// Cloquet / Esko / Proctor MN / Thunder Bay ON / two street addresses)
// all scored 1.0. Real near-miss typos still resolved acceptably (0.96
// for "Dulth, MN"). Nonsense inputs scored 0.5-0.875. 0.9 sits in the gap
// between those two populations. It does NOT catch a plausible-looking
// wrong address that happens to score 1.0 ("123 Main St", literal
// "nowhere") -- that's what placeName is for; no numeric threshold can
// close that gap, since the input isn't distinguishable from a valid
// address by relevance score alone.
export const GEOCODE_RELEVANCE_THRESHOLD = 0.9;

export async function resolveLocationForSave(value) {
  const lat = parseLocationLat(value.locationLat);
  const lng = parseLocationLng(value.locationLng);
  const hasValidCoords = isValidLat(lat) && isValidLng(lng);
  const address = (value.locationAddress || '').trim();

  if (hasValidCoords) {
    return { address: address || null, lat, lng, source: 'entered', placeName: null };
  }

  if (!address) {
    // Neither coordinates nor an address -- isLocationSatisfied should
    // already have blocked Save before this is ever called, but return
    // nulls rather than throw so a caller that skips the check doesn't
    // crash on an assertion that belongs to a different function.
    return { address: null, lat: null, lng: null, source: null, placeName: null };
  }

  const token = import.meta.env.VITE_MAPBOX_TOKEN || '';
  // ASS-82 follow-up: country=us,ca -- this app has no international
  // customers today; restricting the search cuts down on wrong-country
  // matches for ambiguous short queries.
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?limit=1&country=us,ca&access_token=${token}`;

  const response = await fetch(url);

  if (response.status === 401) {
    // Per the brief: stop and say so, don't swap tokens or retry silently.
    throw new GeocodeAuthError('Mapbox token rejected (401) -- check VITE_MAPBOX_TOKEN.');
  }

  if (!response.ok) {
    throw new GeocodeNotFoundError(`Geocoding request failed (${response.status}).`);
  }

  const data = await response.json();
  const feature = data?.features?.[0];
  const center = feature?.center; // Mapbox returns [lng, lat]

  if (!center || center.length !== 2) {
    throw new GeocodeNotFoundError(`No results for "${address}".`);
  }

  // ASS-82 follow-up: relevance guard. A low-relevance match (Mapbox's
  // best guess at an address that doesn't really exist) is treated
  // identically to zero results -- same error type, same copy.
  if (typeof feature.relevance === 'number' && feature.relevance < GEOCODE_RELEVANCE_THRESHOLD) {
    throw new GeocodeNotFoundError(`Low-confidence match for "${address}" (relevance ${feature.relevance}).`);
  }

  const [geocodedLng, geocodedLat] = center;
  return { address, lat: geocodedLat, lng: geocodedLng, source: 'geocoded', placeName: feature.place_name || null };
}

const ProjectDetailsForm = ({ value, onChange, locationRequired = true }) => {
  const patch = (fields) => onChange({ ...value, ...fields });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', gap: 4, mb: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>Type:</Typography>
          {['project', 'yard'].map((t) => (
            <Button
              key={t}
              size="small"
              variant={value.type === t ? 'contained' : 'outlined'}
              onClick={() => patch({ type: t })}
              sx={{ textTransform: 'capitalize', minWidth: 80 }}
            >
              {t}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>Status:</Typography>
          {['active', 'inactive'].map((s) => (
            <Button
              key={s}
              size="small"
              variant={value.status === s ? 'contained' : 'outlined'}
              onClick={() => patch({ status: s })}
              sx={{ textTransform: 'capitalize', minWidth: 80 }}
            >
              {s}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ASS-80 follow-up: Name gets its own full-width row -- the
          previous single row (Name + Client + two DatePickers) needed
          ~568px of minimums and squeezed Name to ~28px inside the Edit
          dialog's maxWidth="sm" (~600px total, minus padding). Client and
          the dates now share a row with flexWrap so they wrap rather than
          compress at narrow widths -- reads well in both the wizard (md)
          and the Edit dialog (sm). */}
      <TextField
        label="Project/Yard Name"
        value={value.name}
        onChange={(e) => patch({ name: e.target.value })}
        fullWidth
        required
        autoFocus
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Client"
          value={value.client}
          onChange={(e) => patch({ client: e.target.value })}
          sx={{ minWidth: 200, flex: '1 1 200px' }}
        />
        <DatePicker
          label="Start Date"
          value={value.startDate ? dayjs(value.startDate) : null}
          onChange={(newValue) => patch({ startDate: newValue ? newValue.format('YYYY-MM-DD') : '' })}
          slotProps={{ textField: { sx: { minWidth: 160, flex: '1 1 160px' } } }}
        />
        <DatePicker
          label="End Date"
          value={value.endDate ? dayjs(value.endDate) : null}
          onChange={(newValue) => patch({ endDate: newValue ? newValue.format('YYYY-MM-DD') : '' })}
          slotProps={{ textField: { sx: { minWidth: 160, flex: '1 1 160px' } } }}
        />
      </Box>

      <TextField
        label="Project/Yard Description"
        value={value.description}
        onChange={(e) => patch({ description: e.target.value })}
        fullWidth
        multiline
        rows={3}
      />

      {/* R-21 / ASS-80 item 5: location is required for Manual projects --
          an address, or both coordinates, or both. For Beacon/Both
          projects, location is optional (their KMZ boundary satisfies
          R-22 on its own) -- gated on locationRequired, which the caller
          derives from trackingMode (wizard: isManualProject; Edit modal:
          editingProject.trackingMode === 'manual'). No KMZ, no drop-a-pin
          (N6 deferred), on either path. */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {locationRequired ? 'Location (required)' : 'Location (optional)'}
        </Typography>
        {locationRequired && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Enter an address, or a latitude and longitude — or both.
          </Typography>
        )}
        <TextField
          label="Address"
          value={value.locationAddress}
          onChange={(e) => {
            // ASS-82 fix: if this location was previously geocoded, changing
            // the address must also clear the stale lat/lng -- otherwise
            // "Duluth, MN" -> "Minneapolis, MN" would keep the Duluth point
            // (resolveLocationForSave sees valid coordinates and takes the
            // "entered coordinates win" branch, never re-geocoding).
            if (value.locationSource === 'geocoded') {
              patch({ locationAddress: e.target.value, locationSource: undefined, locationLat: '', locationLng: '', locationPlaceName: undefined });
            } else {
              patch({ locationAddress: e.target.value, locationSource: undefined, locationPlaceName: undefined });
            }
          }}
          fullWidth
          placeholder="e.g. 123 Main St, Duluth, MN"
          sx={{ mb: 2 }}
        />
        {value.locationSource === 'geocoded' ? (
          // ASS-82: after a successful geocoded save, show the resolved
          // coordinates read-only rather than as editable fields -- they
          // came from the address, not from direct entry, and re-editing
          // them here without also clearing the address would desync the
          // two. Editing the address again clears locationSource (above)
          // and re-enables geocoding on the next Save.
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {value.locationLat}, {value.locationLng}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {/* ASS-82 follow-up: show the resolved place name when we have
                  one, so the user can catch a wrong-but-plausible match
                  ("123 Main St" resolving to the wrong city) that no
                  relevance threshold can distinguish from a real address.
                  Older projects saved before this field existed keep the
                  original, name-less copy. */}
              {value.locationPlaceName
                ? `Located from the address: ${value.locationPlaceName} — check the pin on the map.`
                : 'Located from the address — check the pin on the map.'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* AST-95: text, not type="number" -- a number input silently
                drops a pasted typographic minus. inputMode keeps the
                decimal keypad on mobile; the value is normalised on change. */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Latitude"
                value={value.locationLat}
                onChange={(e) => patch({ locationLat: normaliseCoordinateInput(e.target.value) })}
                inputProps={{ inputMode: 'decimal', 'aria-label': 'Latitude' }}
                error={value.locationLat !== '' && value.locationLat != null && !isValidLat(parseLocationLat(value.locationLat))}
                helperText={value.locationLat !== '' && value.locationLat != null && !isValidLat(parseLocationLat(value.locationLat)) ? 'Between -90 and 90' : ' '}
                fullWidth
                placeholder="46.7867"
              />
              <TextField
                label="Longitude"
                value={value.locationLng}
                onChange={(e) => patch({ locationLng: normaliseCoordinateInput(e.target.value) })}
                inputProps={{ inputMode: 'decimal', 'aria-label': 'Longitude' }}
                error={value.locationLng !== '' && value.locationLng != null && !isValidLng(parseLocationLng(value.locationLng))}
                helperText={value.locationLng !== '' && value.locationLng != null && !isValidLng(parseLocationLng(value.locationLng)) ? 'Between -180 and 180' : ' '}
                fullWidth
                placeholder="-92.1005"
              />
            </Box>
            {(() => {
              const w = coordinateWarning(value.locationLat, value.locationLng);
              if (!w) return null;
              return (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  action={w.suggestion && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => patch({ locationLat: String(w.suggestion.lat), locationLng: String(w.suggestion.lng) })}
                    >
                      Use {w.suggestion.lat}, {w.suggestion.lng}
                    </Button>
                  )}
                >
                  {w.message} You can still save it as entered.
                </Alert>
              );
            })()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ProjectDetailsForm;
